import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

type LanguageContextType = {
  language: string;
  changeLanguage: (lang: string) => Promise<void>;
  availableLanguages: { code: string; name: string; nativeName: string }[];
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const languages = [
  { code: 'pt-BR', name: 'Portuguese (Brazil)', nativeName: 'Português (Brasil)' },
  { code: 'en-US', name: 'English (United States)', nativeName: 'English (United States)' }
];

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [language, setLanguage] = useState<string>(() => {
    const stored = localStorage.getItem('language');
    return stored || 'pt-BR';
  });
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);

  useEffect(() => {
    const initLanguage = async () => {
      const storedLang = localStorage.getItem('language') || 'pt-BR';
      try {
        await i18n.changeLanguage(storedLang);
        setLanguage(storedLang);
      } catch (error) {
        console.error('Error initializing language:', error);
      }
    };

    initLanguage();
  }, []);

  useEffect(() => {
    if (user && !isLoadingPreferences) {
      loadUserLanguage();
    }
  }, [user]);

  const loadUserLanguage = async () => {
    if (!user || isLoadingPreferences) return;

    setIsLoadingPreferences(true);

    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('preferred_language')
        .eq('user_id', user.id)
        .maybeSingle();

      if (data && !error && data.preferred_language) {
        const lang = data.preferred_language;
        if (lang !== language) {
          setLanguage(lang);
          await i18n.changeLanguage(lang);
          localStorage.setItem('language', lang);
        }
      } else if (!error || error.code === 'PGRST116') {
        const defaultLang = language || 'pt-BR';
        try {
          await supabase.from('user_preferences').insert({
            user_id: user.id,
            preferred_language: defaultLang
          });
        } catch (insertError) {
          console.error('Error creating user preferences:', insertError);
        }
      }
    } catch (error) {
      console.error('Error loading user language preferences:', error);
    } finally {
      setIsLoadingPreferences(false);
    }
  };

  const changeLanguage = async (lang: string) => {
    try {
      setLanguage(lang);
      await i18n.changeLanguage(lang);
      localStorage.setItem('language', lang);

      if (user) {
        const { error } = await supabase
          .from('user_preferences')
          .upsert({
            user_id: user.id,
            preferred_language: lang
          }, {
            onConflict: 'user_id'
          });

        if (error) {
          console.error('Error saving language preference:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error changing language:', error);
      throw error;
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, availableLanguages: languages }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
